import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Key, Settings, Terminal, Plus, ChevronDown, ChevronRight,
  Check, Copy, CheckCheck,
} from 'lucide-react';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute right-2 top-2 rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
      title="Copy to clipboard"
    >
      {copied ? <CheckCheck className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function CodeBlock({ code, language }: { code: string; language?: string }) {
  return (
    <div className="relative mt-2 rounded-lg bg-slate-900 p-4">
      <CopyButton text={code} />
      {language && (
        <span className="absolute left-3 top-1.5 text-[10px] uppercase text-slate-500">{language}</span>
      )}
      <pre className="mt-2 overflow-x-auto font-mono text-xs leading-relaxed text-slate-300">{code}</pre>
    </div>
  );
}

interface StepProps {
  number: number;
  title: string;
  icon: typeof Key;
  completed?: boolean;
  children: React.ReactNode;
}

function Step({ number, title, icon: Icon, completed, children }: StepProps) {
  const [expanded, setExpanded] = useState(number === 1);

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-3 px-5 py-4 text-left"
      >
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          completed ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-500'
        }`}>
          {completed ? <Check className="h-4 w-4" /> : <span className="text-sm font-medium">{number}</span>}
        </div>
        <div className="flex flex-1 items-center gap-2">
          <Icon className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-900">{title}</span>
        </div>
        {expanded
          ? <ChevronDown className="h-4 w-4 text-slate-400" />
          : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>
      {expanded && (
        <div className="border-t border-slate-100 px-5 py-4">
          {children}
        </div>
      )}
    </div>
  );
}

interface TeamsOnboardingProps {
  onCreateTeam: () => void;
}

export default function TeamsOnboarding({ onCreateTeam }: TeamsOnboardingProps) {
  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 text-center">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
          <Terminal className="h-7 w-7 text-slate-600" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900">Get started with Teams</h2>
        <p className="mt-1 text-sm text-slate-500">
          Teams let you run multiple AI agents in parallel, coordinated on a shared project.
        </p>
      </div>

      <div className="space-y-3">
        <Step number={1} title="Generate an API key" icon={Key}>
          <p className="text-sm text-slate-600">
            You need an API key to connect your MCP client. Go to Settings to create one.
          </p>
          <Link
            to="/settings"
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            <Key className="h-3.5 w-3.5" />
            Go to Settings
          </Link>
        </Step>

        <Step number={2} title="Configure your MCP client" icon={Settings}>
          <p className="mb-3 text-sm text-slate-600">
            Add the following to your Claude Code MCP configuration (or any MCP-compatible client):
          </p>
          <CodeBlock
            language="json"
            code={`{
  "mcpServers": {
    "aldan": {
      "type": "streamableHttp",
      "url": "${window.location.origin}/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`}
          />
          <p className="mt-3 text-xs text-slate-400">
            Replace <code className="rounded bg-slate-100 px-1 py-0.5 text-slate-600">YOUR_API_KEY</code> with the key you generated in step 1.
          </p>
        </Step>

        <Step number={3} title="Create a team via MCP" icon={Terminal}>
          <p className="mb-3 text-sm text-slate-600">
            Use the MCP tools to create a team with agents assigned to it:
          </p>
          <CodeBlock
            language="json"
            code={`// 1. List your agents
{ "tool": "list_agents", "args": { "project_id": "..." } }

// 2. Create a team with agent IDs
{
  "tool": "create_team",
  "args": {
    "project_id": "...",
    "name": "My Team",
    "agent_ids": ["agent-uuid-1", "agent-uuid-2"]
  }
}`}
          />
        </Step>

        <Step number={4} title="Or create directly from the UI" icon={Plus}>
          <p className="mb-3 text-sm text-slate-600">
            You can also create a team right here. Select agents from your project and launch them as a coordinated team.
          </p>
          <button
            onClick={onCreateTeam}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            <Plus className="h-4 w-4" />
            Create Team
          </button>
        </Step>
      </div>
    </div>
  );
}
